import { actions, makePropGetter, useGetLatest } from 'react-table';
export function useFlexLayout(hooks) {
  hooks.useInstanceBeforeDimensions.push(useInstanceBeforeDimensions);
  hooks.getTableProps.push(getTableProps);
  hooks.getRowProps.push(getRowStyles);
  hooks.getHeaderGroupProps.push(getRowStyles);
  hooks.getFooterGroupProps.push(getRowStyles);
  hooks.getHeaderProps.push(getHeaderProps);
  hooks.getCellProps.push(getCellProps);
  hooks.getFooterProps.push(getFooterProps);
  hooks.getTableBodyProps.push(getBodyProps);
  hooks.stateReducers.push(reducer);
}
useFlexLayout.pluginName = 'useFlexLayout';
const getTableProps = (props, {
  instance
}) => [props, {
  style: {
    maxWidth: `${instance.totalColumnsMaxWidth}px`
  }
}];
const getBodyProps = (props, {
  instance
}) => {
  return [props, {
    style: {
      minWidth: `${Math.max(instance.totalColumnsMinWidth, instance.totalColumnsWidth)}px`
    }
  }];
};
const getRowStyles = (props, {
  instance
}) => [props, {
  style: {
    display: 'flex',
    flex: '1 0 auto',
    minWidth: `${instance.totalColumnsMinWidth}px`
  }
}];
const getWidth = (id, includeHeader = false) => {
  let b = [];
  if (includeHeader) {
    b.push(document.querySelector(`.${id}-header`)?.getBoundingClientRect?.()?.width ?? 0);
  }
  document.querySelectorAll(`.${id}-cell`).forEach(i => b.push(i?.parentNode?.getBoundingClientRect?.()?.width ?? 0));
  return Math.max(...b) ?? 'max-content';
};
const getHeaderProps = (props, {
  column,
  instance,
  ...rest
}) => {
  const {
    state: {
      columnResizing: {
        columnWidths = {}
      } = {}
    } = {}
  } = instance;
  return [props, {
    style: {
      boxSizing: 'border-box',
      flex: `${column.totalFlexWidth} 0 auto`,
      minWidth: column.collapsed ? `${column.totalWidth}px` : `${column.totalMinWidth}px`,
      width: `${column.totalWidth}px`,
      ...((column.totalWidth !== column.originalWidth || !!columnWidths[column.id] || column.maxWidth !== Number.MAX_SAFE_INTEGER) && {
        maxWidth: `${Math.min(column.totalWidth, column.totalMaxWidth)}px`
      }),
      alignSelf: 'center'
    }
  }];
};
const getCellProps = (props, {
  cell,
  instance,
  ...rest
}) => {
  const {
    state: {
      columnResizing: {
        columnWidths = {}
      } = {}
    } = {}
  } = instance;
  return [props, {
    style: {
      boxSizing: 'border-box',
      flex: `${cell.column.totalFlexWidth} 0 auto`,
      minWidth: cell.column.collapsed ? `${cell.column.totalWidth}px` : `${cell.column.totalMinWidth}px`,
      width: cell.column.collapsed ? `${cell.column.totalWidth}px` : `${cell.column.totalWidth}px`,
      ...((cell.column.totalWidth !== cell.column.originalWidth || columnWidths[cell.column.id] || cell.column.maxWidth !== Number.MAX_SAFE_INTEGER) && {
        maxWidth: `${Math.min(cell.column.totalWidth, cell.column.totalMaxWidth)}px`
      }),
      alignSelf: 'center'
    }
  }];
};
const getFooterProps = (props, {
  column
}) => [props, {
  style: {
    boxSizing: 'border-box',
    flex: column.totalFlexWidth ? `${column.totalFlexWidth} 0 auto` : undefined,
    minWidth: `${column.totalMinWidth}px`,
    width: `${column.totalWidth}px`
  }
}];
actions.columnStartResizing = 'columnStartResizing';
actions.columnResizing = 'columnResizing';
actions.columnDoneResizing = 'columnDoneResizing';
actions.resetResize = 'resetResize';
function reducer(state, action) {
  // console.log(state, action);
  if (action.type === actions.init) {
    return {
      columnResizing: {
        columnWidths: {}
      },
      ...state
    };
  }
  if (action.type === actions.resetResize) {
    return {
      ...state,
      columnResizing: {
        columnWidths: {}
      }
    };
  }
  if (action.type === actions.columnStartResizing) {
    const {
      clientX,
      columnId,
      headerIdWidths
    } = action;
    // console.log(
    //   action,
    //   columnId,
    //   document.getElementById(columnId)?.getBoundingClientRect?.()?.width
    // );
    let width = document.getElementById(columnId)?.getBoundingClientRect?.()?.width;
    let newColumnWidths = {};
    headerIdWidths.forEach(([headerId, headerWidth]) => {
      newColumnWidths[headerId] = width;
    });
    // console.log(newColumnWidths, headerIdWidths, width);
    return {
      ...state,
      columnResizing: {
        ...state.columnResizing,
        startX: clientX,
        headerIdWidths,
        columnWidth: width,
        isResizingColumn: columnId,
        columnWidths: {
          ...state.columnResizing.columnWidths,
          ...newColumnWidths
        }
      }
    };
  }
  if (action.type === actions.columnResizing) {
    const {
      clientX
    } = action;
    const {
      startX,
      columnWidth,
      headerIdWidths = []
    } = state.columnResizing;
    const deltaX = clientX - startX;
    const percentageDeltaX = deltaX / columnWidth;
    const newColumnWidths = {};
    headerIdWidths.forEach(([headerId, headerWidth]) => {
      newColumnWidths[headerId] = Math.max(columnWidth + columnWidth * percentageDeltaX, 0);
    });
    return {
      ...state,
      columnResizing: {
        ...state.columnResizing,
        columnWidths: {
          ...state.columnResizing.columnWidths,
          ...newColumnWidths
        }
      }
    };
  }
  if (action.type === actions.columnDoneResizing) {
    return {
      ...state,
      columnResizing: {
        ...state.columnResizing,
        startX: null,
        isResizingColumn: null
      }
    };
  }
}
function getFirstDefined(...args) {
  for (let i = 0; i < args.length; i += 1) {
    if (typeof args[i] !== 'undefined') {
      return args[i];
    }
  }
}
const useInstanceBeforeDimensions = instance => {
  const {
    flatHeaders,
    disableResizing,
    getHooks,
    state: {
      columnResizing
    }
  } = instance;
  const getInstance = useGetLatest(instance);
  // console.log('hereree', { ...instance });
  flatHeaders.forEach(header => {
    const canResize = getFirstDefined(header.disableResizing === true ? false : undefined, disableResizing === true ? false : undefined, true);
    header.canResize = canResize;
    header.width = columnResizing.columnWidths[header.id] ?? (header.originalWidth || header.width);
    header.isResizing = columnResizing.isResizingColumn === header.id;
    // debugger;
    // if (header.collapsed) {
    //   let newWidth = getWidth(header.id);
    //   header.minWidth = newWidth;
    //   header.width = newWidth;
    //   header.maxWidth = newWidth;
    // }
    if (canResize) {
      header.getResizerProps = makePropGetter(getHooks().getResizerProps, {
        instance: getInstance(),
        header
      });
    }
  });
};